import axios from "axios";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";

import { useAuth } from "context/AuthContext";
import { getSessionCompanies, getSessionToken } from "utils/session";

import { useToast } from "./useToast";

const INTEGRADOR_BASE_URL =
  process.env.REACT_APP_INTEGRADOR_API ||
  "https://api.mixfiscal.com.br/integrador/api/v1";

export const useApiIntegrador = () => {
  const onRejected = error => {
    return Promise.reject(error);
  };

  const apiRef = useRef(
    axios.create({
      baseURL: INTEGRADOR_BASE_URL,
    }),
  );

  apiRef.current.interceptors.request.use(config => {
    // API do integrador exige o prefixo "Bearer" no header de autorização.
    const token = getSessionToken();
    config.headers.Authorization = token?.startsWith("Bearer ")
      ? token
      : `Bearer ${token}`;

    // O token de autenticação só carrega e-mail e id; o de empresas carrega o
    // nome do usuário. Vai neste header à parte para o backend registrar quem
    // executou a ação (ex.: created_by dos agendamentos) sem depender do corpo
    // da requisição e sem trocar o token de autenticação da API inteira.
    const userToken = getSessionCompanies();
    if (userToken) {
      config.headers["X-User-Token"] = userToken;
    }

    return config;
  });

  apiRef.current.interceptors.response.use(response => response, onRejected);

  return apiRef.current;
};

export const useFetchIntegrador = (url, axiosConfigs, config) => {
  const api = useApiIntegrador();
  const { tokenCompanies, revalidateAll, setRevalidateAll } = useAuth();

  const { data, error, mutate, revalidate, isValidating } = useSWR(
    url,
    async urlReturn => {
      const response = await api.get(urlReturn, axiosConfigs);
      return response.data;
    },
    config,
  );

  // Revalida todas as requisicoes para carregar os dados corretamente!
  useEffect(() => {
    if (revalidateAll?.length) {
      revalidate();
      setRevalidateAll();
    }
  }, [tokenCompanies, revalidate, revalidateAll, setRevalidateAll]);

  return {
    data,
    error,
    isError: !!error,
    isLoading: (!error && !data && String(data) !== "") || isValidating,
    isValidating,
    mutate,
    revalidate,
  };
};

export const useApiIntegradorToast = apiRef => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({});
  const api = useApiIntegrador();
  const { addToast } = useToast();

  const onSend = async ({
    messageSuccess,
    messageFail,
    endpoint,
    type = "POST",
    data: dataFields = {},
  }) => {
    try {
      setIsLoading(true);
      const { data } = await (apiRef ?? api)({
        method: type,
        url: endpoint,
        data: dataFields,
      });

      addToast({
        type: "success",
        title: "SUCESSO",
        description: messageSuccess,
      });
      setData(data);
      setIsLoading(false);
      return data;
    } catch (err) {
      setData(err);
      addToast({
        type: "error",
        title: "FALHA",
        description: messageFail,
      });
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    data,
    onSend,
  };
};
